import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    useDisclosure,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { publicBoatService } from "services/publicBoatService";
import { ROUTES } from "constants/routes-path";
import { AuthContext } from "contexts/AuthContext";
import {
    acidenteLabels,
    caracteristicaDaPistaLabels,
    condicaoDoTempoLabels,
    getSelectedLabels,
    luminosidadeLabels,
    pistaLabels,
    sinalizacaoLabels,
    tipoDaPistaLabels,
} from "utils/boat-utils";

function PublicBoatDetails() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const { user } = useContext(AuthContext);
    const boat = state?.boat;
    const declarantData = boat?.declarantData;

    const parsedBoatData = (() => {
        const raw = boat?.boatData;
        if (!raw) return null;
        if (typeof raw === "object") return raw;
        try { return JSON.parse(raw); } catch { return null; }
    })();

    const { images, videos } = boat || {};
    const { local, acidente, condicoes, veiculos, versoes, observacoes, desenho } = parsedBoatData || {};
    const { condicaoDoTempo, luminosidade, sinalizacao, tipoDaPista, caracteristicaDaPista, pista } = condicoes || {};
    const { socorrista, agenteTransito, agentesEnvolvidos } = observacoes || {};

    const naturezaOcorrencia = getSelectedLabels(acidente, acidenteLabels);
    const condicoesDoTempo = getSelectedLabels(condicaoDoTempo, condicaoDoTempoLabels);
    const condicoesLuminosidade = getSelectedLabels(luminosidade, luminosidadeLabels);
    const condicoesSinalizacao = getSelectedLabels(sinalizacao, sinalizacaoLabels);
    const tiposDePista = getSelectedLabels(tipoDaPista, tipoDaPistaLabels);
    const caracteristicasPista = getSelectedLabels(caracteristicaDaPista, caracteristicaDaPistaLabels);
    const condicoesDaPista = getSelectedLabels(pista, pistaLabels);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = React.useRef();

    const [pendingStatus, setPendingStatus] = useState(null);
    const [observation, setObservation] = useState("");
    const [observationError, setObservationError] = useState(false);
    const [loading, setLoading] = useState(false);

    const openConfirm = (status) => {
        setPendingStatus(status);
        setObservation("");
        setObservationError(false);
        onOpen();
    };

    const handleConfirm = async () => {
        if (pendingStatus === "REJEITADO" && !observation.trim()) {
            setObservationError(true);
            return;
        }

        setLoading(true);
        try {
            await publicBoatService.update(boat.id, {
                status: pendingStatus,
                observation: observation.trim() || undefined,
                agentId: user?.agente_id,
            });
            showToast({ title: "BOAT atualizado com sucesso.", status: "success" });
            navigate(ROUTES.publicBoatsPath);
        } catch (err) {
            showToast({ title: err, status: "error" });
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const formatCpf = (value) => {
        if (!value) return value;
        return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    };

    const formatTelefone = (value) => {
        if (!value) return value;
        const digits = value.replace(/\D/g, "");
        if (digits.length === 11)
            return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
        if (digits.length === 10)
            return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
        return value;
    };

    if (!boat) {
        return (
            <Flex direction="column" pt={{ sm: "125px", lg: "75px" }} w="100%">
                <Text px="40px">BOAT não encontrado.</Text>
            </Flex>
        );
    }

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }} w="100%">
            <Card px="0px" pt="10px" mb="24px" bg="rgba(255, 255, 255, 0.7)">
                <CardHeader px="22px" mb="24px">
                    <Flex justifyContent="space-between" gap="3">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => navigate(ROUTES.publicBoatsPath)}
                        >
                            Voltar
                        </Button>
                        <HStack spacing="3" mt="24px">
                            <Button
                                fontSize="sm"
                                colorScheme="green"
                                leftIcon={<CheckIcon />}
                                onClick={() => openConfirm("ACEITO")}
                            >
                                Aceitar
                            </Button>
                            <Button
                                fontSize="sm"
                                colorScheme="red"
                                leftIcon={<CloseIcon />}
                                onClick={() => openConfirm("REJEITADO")}
                            >
                                Rejeitar
                            </Button>
                        </HStack>
                    </Flex>
                </CardHeader>

                <CardBody m="10px" pb="40px" bg="#fff" style={{ borderRadius: "25px" }}>
                    <Flex gap="4" justify="space-between" pt="20px" px="40px" w="100%">
                        <Text fontSize="lg" fontWeight="bold">BOST Público</Text>
                        <Text fontSize="lg" fontWeight="bold">Núm. # {boat.boatNumber}</Text>
                    </Flex>

                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300" />
                    </Flex>

                    <Flex gap="4" justify="flex-start" px="40px">
                        <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }} w="100%">
                            <Text fontWeight="bold">Data de Solicitação:</Text>
                            <Text>{new Date(boat.createdAt).toLocaleString()}</Text>
                            <Text fontWeight="bold">Status:</Text>
                            <Badge
                                colorScheme={
                                    boat.status === "ACEITO" ? "green" :
                                    boat.status === "REJEITADO" ? "red" : "yellow"
                                }
                                borderRadius="full"
                                px="3"
                                py="1"
                                fontSize="sm"
                                alignSelf="center"
                                maxW="fit-content"
                            >
                                {boat.status}
                            </Badge>
                            {boat.observation && (
                                <>
                                    <Text fontWeight="bold">Observação:</Text>
                                    <Text>{boat.observation}</Text>
                                </>
                            )}
                        </SimpleGrid>
                    </Flex>
                    
                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300" />
                    </Flex>

                    <Flex gap="4" justify="flex-start" px="40px" direction="column">
                        <Text fontWeight="bold">Dados do Declarante</Text>
                            <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }} w="100%">
                                {declarantData.nome && <><Text fontWeight="bold">Nome:</Text><Text>{declarantData.nome}</Text></>}
                                {declarantData.cpf && <><Text fontWeight="bold">CPF:</Text><Text>{formatCpf(declarantData.cpf)}</Text></>}
                                {declarantData.email && <><Text fontWeight="bold">E-mail:</Text><Text>{declarantData.email}</Text></>}
                                {declarantData.telefone && <><Text fontWeight="bold">Telefone:</Text><Text>{formatTelefone(declarantData.telefone)}</Text></>}
                            </SimpleGrid>
                    </Flex>

                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300" />
                    </Flex>

                    <Flex gap="4" justify="flex-start" px="40px" direction="column">
                        <Text fontWeight="bold">Dados do BOAT</Text>
                    </Flex>

                    {parsedBoatData && (
                        <>
                            <Flex gap="4" justify="" px="40px" mt="10px">
                                <Flex gap="4" justify="flex-start" w="100%">
                                    <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }}>
                                        <Text fontWeight="bold">Local</Text>
                                        <Text></Text>
                                        <Text>Local:</Text>
                                        <Text>{local?.local}</Text>
                                        <Text>Bairro:</Text>
                                        <Text>{local?.bairro}</Text>
                                        <Text>Cidade/UF:</Text>
                                        <Text>{`${local?.cidade || '--'}/${local?.uf || '--'}`}</Text>
                                        <Text>Ponto de Referência:</Text>
                                        <Text>{local?.pontoReferencia || 'N/A'}</Text>
                                        <Text>Data/Hora do Acidente:</Text>
                                        <Text>{local?.dataHoraAcidente ? new Date(local.dataHoraAcidente).toLocaleString() : '--'}</Text>
                                    </SimpleGrid>
                                </Flex>
                                <Flex gap="4" justify="flex-start" w="100%">
                                    <SimpleGrid columns={1} spacing={2}>
                                        <Text fontWeight="bold">Natureza da Ocorrência</Text>
                                        <Box>
                                            {naturezaOcorrencia?.length === 0 && <Badge>N/A</Badge>}
                                            {naturezaOcorrencia?.map((v, i) => (
                                                <Badge key={`natureza-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                        <Text fontWeight="bold">Condições do Tempo</Text>
                                        <Box>
                                            {condicoesDoTempo?.length === 0 && <Badge>N/A</Badge>}
                                            {condicoesDoTempo?.map((v, i) => (
                                                <Badge key={`tempo-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                        <Text fontWeight="bold">Luminosidade</Text>
                                        <Box>
                                            {condicoesLuminosidade?.length === 0 && <Badge>N/A</Badge>}
                                            {condicoesLuminosidade?.map((v, i) => (
                                                <Badge key={`lum-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                        <Text fontWeight="bold">Sinalização</Text>
                                        <Box>
                                            {condicoesSinalizacao?.length === 0 && <Badge>N/A</Badge>}
                                            {condicoesSinalizacao?.map((v, i) => (
                                                <Badge key={`sin-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                    </SimpleGrid>
                                </Flex>
                                <Flex gap="4" justify="flex-start" w="100%">
                                    <SimpleGrid columns={1} spacing={2}>
                                        <Text fontWeight="bold">Tipo da Pista</Text>
                                        <Box>
                                            {tiposDePista?.length === 0 && <Badge>N/A</Badge>}
                                            {tiposDePista?.map((v, i) => (
                                                <Badge key={`tipo-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                        <Text fontWeight="bold">Características da Pista</Text>
                                        <Box>
                                            {caracteristicasPista?.length === 0 && <Badge>N/A</Badge>}
                                            {caracteristicasPista?.map((v, i) => (
                                                <Badge key={`carac-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                        <Text fontWeight="bold">Condições da Pista</Text>
                                        <Box>
                                            {condicoesDaPista?.length === 0 && <Badge>N/A</Badge>}
                                            {condicoesDaPista?.map((v, i) => (
                                                <Badge key={`pista-${i}`} variant="solid" colorScheme="purple" mr="5px">{v}</Badge>
                                            ))}
                                        </Box>
                                    </SimpleGrid>
                                </Flex>
                            </Flex>

                            <Flex gap="4" justify="center" py="20px">
                                <Divider borderColor="gray.300" />
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <SimpleGrid columns={1} spacing={2}>
                                    <Text fontWeight="bold">Veículos</Text>
                                </SimpleGrid>
                            </Flex>

                            {veiculos?.map((v, index) => (
                                <Flex key={`vehicle-${index}`} gap="4" justify="flex-start" px="40px" mt="20px">
                                    <SimpleGrid spacing={2} flex="1" columns={{ sm: 1, md: 2 }}>
                                        <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }}>
                                            <Text fontWeight="bold">Veículo {index + 1}</Text>
                                            <Text></Text>
                                            <Text>Placa:</Text>
                                            <Box><Badge variant="solid" colorScheme="purple">{v.placa}</Badge></Box>
                                            <Text>Proprietário:</Text>
                                            <Text>{v.proprietario || '--'}</Text>
                                            <Text>Marca/Modelo:</Text>
                                            <Text>{v.marcaModelo || '--'}</Text>
                                            <Text>Tipo:</Text>
                                            <Text>{v.tipo || '--'}</Text>
                                            <Text>Cor:</Text>
                                            <Text>{v.cor || '--'}</Text>
                                            <Text>Combustível:</Text>
                                            <Text>{v.combustivel || '--'}</Text>
                                            <Text>UF:</Text>
                                            <Text>{v.uf || '--'}</Text>
                                            <Text>Nº de Ocupantes:</Text>
                                            <Text>{v.numeroOcupantes || '--'}</Text>
                                        </SimpleGrid>
                                        <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }} flex="1">
                                            <Text fontWeight="bold">Condutor</Text>
                                            <Text></Text>
                                            <Text>Nome:</Text>
                                            <Text>{v.condutor?.nome || '--'}</Text>
                                            <Text>Data de Nascimento:</Text>
                                            <Text>{v.condutor?.dataNascimento || '--'}</Text>
                                            <Text>Endereço:</Text>
                                            <Text>{v.condutor?.endereco || '--'}</Text>
                                            <Text>Telefone:</Text>
                                            <Text>{v.condutor?.telefone || '--'}</Text>
                                            <Text>CPF:</Text>
                                            <Text>{v.condutor?.cpf || '--'}</Text>
                                            <Text>CNH:</Text>
                                            <Text>{v.condutor?.cnh || '--'}</Text>
                                            <Text>Validade CNH:</Text>
                                            <Text>{v.condutor?.validadeCnh || '--'}</Text>
                                            <Text>Categoria CNH:</Text>
                                            <Text>{v.condutor?.categoriaCnh || '--'}</Text>
                                        </SimpleGrid>
                                    </SimpleGrid>
                                </Flex>
                            ))}

                            {versoes?.length > 0 && (
                                <>
                                    <Flex gap="4" justify="center" py="20px">
                                        <Divider borderColor="gray.300" />
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px">
                                        <SimpleGrid columns={1} spacing={2}>
                                            <Text fontWeight="bold">Versões</Text>
                                        </SimpleGrid>
                                    </Flex>
                                    {versoes.map((v, index) => (
                                        <Flex key={`versao-${index}`} gap="4" justify="flex-start" px="40px" mt="20px">
                                            <SimpleGrid spacing={2} flex="1" columns={{ sm: 1, md: 2 }}>
                                                <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }}>
                                                    <Text fontWeight="bold">Versão {index + 1}</Text>
                                                    <Text></Text>
                                                    <Text>Nome:</Text>
                                                    <Text>{v.nome || '--'}</Text>
                                                    <Text>Rua:</Text>
                                                    <Text>{v.rua || '--'}</Text>
                                                    <Text>Sentido:</Text>
                                                    <Text>{v.sentido || '--'}</Text>
                                                    <Text>Versão:</Text>
                                                    <Text>{v.versao || '--'}</Text>
                                                </SimpleGrid>
                                            </SimpleGrid>
                                        </Flex>
                                    ))}
                                </>
                            )}

                            <Flex gap="4" justify="center" py="20px">
                                <Divider borderColor="gray.300" />
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <SimpleGrid columns={1} spacing={2}>
                                    <Text fontWeight="bold">Observações</Text>
                                </SimpleGrid>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <SimpleGrid spacing={2} flex="1" columns={{ sm: 1, md: 2 }} pt="15px">
                                    <SimpleGrid spacing={2} columns={{ sm: 1, md: 2 }}>
                                        <Text fontWeight="bold">Socorrista</Text>
                                        <Text></Text>
                                        <Text>Nome:</Text>
                                        <Text>{socorrista?.nome || '--'}</Text>
                                        <Text>Documento:</Text>
                                        <Text>{socorrista?.documentoIdentificacao || '--'}</Text>
                                        <Text>Endereço:</Text>
                                        <Text>{socorrista?.endereco || '--'}</Text>
                                        <Text>Observação:</Text>
                                        <Text>{socorrista?.observacao || '--'}</Text>
                                    </SimpleGrid>
                                    <Stack spacing={2}>
                                        {agentesEnvolvidos?.length > 0 && (
                                            <Text fontWeight="bold">Agentes Envolvidos</Text>
                                        )}
                                        {agentesEnvolvidos?.map((agente, i) => (
                                            <Text key={`agente-${i}`}>{agente.matricula} - {agente.nome}</Text>
                                        ))}
                                    </Stack>
                                </SimpleGrid>
                            </Flex>

                            {images?.length > 0 && (
                                <>
                                    <Flex gap="4" justify="center" py="20px">
                                        <Divider borderColor="gray.300" />
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px">
                                        <Text fontWeight="bold">Imagens</Text>
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px" pt="20px">
                                        {images.map((url, i) => (
                                            <img key={`img-${i}`} alt={`imagem-${i}`} style={{ maxWidth: 250 }} width="100%" height="100%" src={url} />
                                        ))}
                                    </Flex>
                                </>
                            )}

                            {videos?.length > 0 && (
                                <>
                                    <Flex gap="4" justify="center" py="20px">
                                        <Divider borderColor="gray.300" />
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px">
                                        <Text fontWeight="bold">Vídeos</Text>
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px" pt="20px">
                                        {videos.map((url, i) => (
                                            <video key={`video-${i}`} width={150} height={150} controls src={url} />
                                        ))}
                                    </Flex>
                                </>
                            )}

                            {desenho?.image && (
                                <>
                                    <Flex gap="4" justify="center" py="20px">
                                        <Divider borderColor="gray.300" />
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px">
                                        <Text fontWeight="bold">Desenho</Text>
                                    </Flex>
                                    <Flex gap="4" justify="flex-start" px="40px" pt="20px">
                                        <img alt="Desenho" style={{ maxWidth: 400, maxHeight: 400 }} src={desenho.image} />
                                    </Flex>
                                </>
                            )}
                        </>
                    )}

                </CardBody>
            </Card>

            <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {pendingStatus === "ACEITO" ? "Aceitar BOAT" : "Rejeitar BOAT"}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <Text mb="4">
                                {pendingStatus === "ACEITO"
                                    ? "Tem certeza que deseja aceitar este BOAT?"
                                    : "Tem certeza que deseja rejeitar este BOAT?"}
                            </Text>

                            <FormControl isInvalid={observationError}>
                                <FormLabel>
                                    Observação{pendingStatus === "REJEITADO" && " *"}
                                </FormLabel>
                                <Textarea
                                    value={observation}
                                    onChange={(e) => {
                                        setObservation(e.target.value);
                                        if (e.target.value.trim()) setObservationError(false);
                                    }}
                                    placeholder="Digite uma observação..."
                                />
                                {observationError && (
                                    <FormErrorMessage>Observação é obrigatória para rejeição.</FormErrorMessage>
                                )}
                            </FormControl>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <HStack spacing="2">
                                <Button ref={cancelRef} onClick={onClose} isDisabled={loading}>
                                    Cancelar
                                </Button>
                                <Button
                                    colorScheme={pendingStatus === "ACEITO" ? "green" : "red"}
                                    onClick={handleConfirm}
                                    isLoading={loading}
                                >
                                    Confirmar
                                </Button>
                            </HStack>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
}

export default PublicBoatDetails;
